<template>
	<!-- Measured against Figma 7415:20691 (approved), 7417:21296 (review), 7417:21679 (rejected), 7415:17379,
	     7415:15717, 7415:15816, 7415:15934, 7493:5749 (after a cancel), 6624:13750 (paid in full) and
	     6847:86450 / 7045:40272 (paid in full after a cancel). -->
	<LayoutFlex padding="0" gap="0" background="--background-white" corner-radius="var(--size-36)">
		<!-- Plan header (Figma "Text"): name and billing line in one column, the status pill beside it. -->
		<LayoutFlex
			flex-direction="row"
			align-items="flex-start"
			padding="var(--size-24)"
			gap="var(--size-16)"
			min-width="100%"
			:box-shadow="divider('header')"
		>
			<LayoutFlex padding="0" gap="var(--size-8)" align-items="flex-start" min-width="0" grow>
				<Text :font="KOVA.font20x24Semi" color="--text-dark-primary">{{ title }}</Text>
				<Text
					v-if="subtitle"
					font="--text-14-20-med"
					:color="KOVA.textSecondary"
					:class="{ 'commitment-card__one-line': subtitle === billingCycle }"
				>
					{{ subtitle }}
				</Text>
			</LayoutFlex>
			<span v-if="planBadge" class="commitment-card__pill" :class="`commitment-card__pill--${planBadge.tone}`">
				<Text tag="span" font="--text-14-20-med" color="inherit">{{ planBadge.label }}</Text>
			</span>
		</LayoutFlex>

		<!-- The prescription's clinical state (Figma 7415:9856). -->
		<LayoutFlex
			v-if="prescription"
			flex-direction="row"
			justify-content="space-between"
			align-items="center"
			padding="var(--size-16) var(--size-24)"
			gap="var(--size-16)"
			min-width="100%"
			:box-shadow="divider('prescription')"
		>
			<Text font="--text-16-22-semi" color="--text-dark-primary">Your prescription</Text>
			<span class="commitment-card__pill" :class="`commitment-card__pill--${prescription.tone}`">
				<Text tag="span" font="--text-14-20-med" color="inherit">{{ prescription.label }}</Text>
			</span>
		</LayoutFlex>

		<!-- One row per 3-month supply. A monthly commitment: the ones paid for, the current one with its
		     payments, the ones ahead greyed out (Figma note: "depending on the plan can be fewer
		     deliveries"). Paid in full (Figma 6624:13750): every supply of the period paid; shipped or
		     still to come. -->
		<LayoutFlex
			v-if="supplies.length"
			padding="0 var(--size-24)"
			gap="0"
			min-width="100%"
			:box-shadow="divider('supplies')"
		>
			<LayoutFlex
				v-for="(row, index) in supplies"
				:key="row.number"
				padding="var(--size-16) 0"
				gap="var(--size-8)"
				min-width="100%"
				:box-shadow="index < supplies.length - 1 ? KOVA.divider : 'none'"
			>
				<LayoutFlex
					flex-direction="row"
					justify-content="space-between"
					align-items="center"
					padding="0"
					gap="var(--size-8)"
					min-width="100%"
				>
					<Text
						font="--text-16-22-semi"
						:color="row.muted ? KOVA.textDisabled : '--text-dark-primary'"
						:class="{ 'commitment-card__struck': row.struck }"
					>
						Supply {{ row.number }}
					</Text>
					<Text font="--text-16-22-semi" :color="row.muted ? KOVA.textDisabled : '--text-dark-primary'">
						{{ row.label }}
					</Text>
				</LayoutFlex>
				<Text v-if="row.fullyPaid" font="--text-14-20-med" :color="KOVA.textSecondary60">Fully paid</Text>
				<!-- Figma: 4 more above the numbers than between the rows' lines. -->
				<div v-if="row.current && q.progress" class="commitment-card__progress">
					<SupplyProgress :paid="q.progress.paid" :total="q.progress.total" tone="brand">
						<template #aside>
							<Text v-if="nextCharge" font="--text-14-20-med" color="--text-dark-primary">
								Next charge: {{ nextCharge }}
							</Text>
						</template>
					</SupplyProgress>
				</div>
			</LayoutFlex>
		</LayoutFlex>

		<!-- Payment method: shown while the plan still bills — also after "Keep my plan", when charges keep
		     coming. Not while it is ending after the instalments (Figma 7415:15717). -->
		<LayoutFlex
			v-if="showPaymentMethod"
			flex-direction="row"
			justify-content="space-between"
			align-items="center"
			padding="var(--size-16) var(--size-24)"
			min-width="100%"
			:box-shadow="divider('payment')"
		>
			<LayoutFlex padding="0" gap="var(--size-8)" align-items="flex-start">
				<Text font="--text-16-22-semi" color="--text-dark-primary">Payment method</Text>
				<img v-if="lockup" :src="lockup.src" :alt="lockup.alt" data-payment-method />
			</LayoutFlex>
			<button type="button" class="commitment-card__link" @click="emit('change-payment-method')">
				<Text tag="span" font="--text-16-22-reg" color="inherit">Update</Text>
			</button>
		</LayoutFlex>

		<!-- Right under the supply being paid off, the buy-out keeps to that block: 12 above, 16 below
		     (Figma 7415:15717); elsewhere a section of its own. -->
		<LayoutFlex v-if="showActions" :padding="actionsPadding" min-width="100%" :box-shadow="divider('actions')">
			<!-- Cancelled in this flow but still offered the buy-out (after "Keep my plan" or the
			     instalments): pay what's left and end today. Figma 7415:15717. -->
			<div v-if="buyOut" class="commitment-card__buy-out">
				<Button hierarchy="secondary" full-width @ds:click="emit('pay-and-cancel')">
					<Text font="--text-16-18-semi">Pay {{ money(buyOut.amount) }} and cancel today</Text>
				</Button>
			</div>
			<Button v-else hierarchy="secondary" full-width @ds:click="emit('manage')">
				<Text font="--text-16-18-semi">Manage subscription</Text>
			</Button>
		</LayoutFlex>

		<!-- Only once the user cancelled (Figma annotation): when access ends. -->
		<LayoutFlex
			v-if="expiration"
			flex-direction="row"
			justify-content="space-between"
			align-items="center"
			:padding="expirationPadding"
			gap="var(--size-16)"
			min-width="100%"
		>
			<Text font="--text-16-22-reg" color="--text-dark-primary">Expiration date</Text>
			<Text font="--text-16-22-reg" color="--text-dark-primary">{{ expiration }}</Text>
		</LayoutFlex>
	</LayoutFlex>
</template>

<script setup lang="ts">
// The /status subscription card for a commitment plan (DEV-35727) — Figma "Kova-Growth → TO BE /
// Cancellation flow" (7415:9856; the live card 6371:5169). Only a commitment (monthly or paid in
// full) gets it, in every state; any other plan keeps SubscriptionCard.vue (owner, 2026-10-22). Status.vue
// picks one. Reads the subscription store itself and emits the actions; navigation and analytics stay on
// the page.
//
// The supplies are billing's `supply_schedule.supplies` (med-billing-engine#319), labelled "Supply N"
// (owner, 2026-10-13: no order numbers). The design's words per supply state (DEV-36625, Figma 7417:21074):
// the carrier's `delivered` "Delivered", `shipped` "In transit", a `cancelled` one "Cancelled" struck out,
// any other state "To Be Delivered"; `delivery_failed` "Delivery failed" until design draws it (owner,
// 2026-10-29). Out of the pharmacy (`SHIPPED_SUPPLY_STATES`) the row is dark, else greyed. "Next charge" is `supply_schedule.next_charge.at` — when the engine actually
// charges, four days before the period ends; on a plan that isn't a commitment `current_period_end_at`.
//
// A paid-in-full plan (`flow.kind === 'upfront'`, Figma 6624:13750) bills one period: "Billing Cycle ·
// $1,548 / 1 year" at the price paid for it, then its supplies — all "Fully paid", delivered ones
// "Delivered". It stays paid in full after a cancel (`term_type`): refunded (Figma 6847:86450) its
// unshipped supplies are struck out "Cancelled"; cancelled at the plan end (7045:40272) it "Expires",
// the rest still to be delivered.
//
// States (docs/architecture/styles.md → CommitmentCard): under review "Pending" + "Clinician review",
// no supplies; rejected "Cancelled" + "Rejected" and nothing else; after a cancel "Cancelled" (ended,
// or ending after the instalments, or refunded) or "Expires {date}" (renewal turned off, or paid in full
// cancelled at the plan end) and the expiration date.
// The "Have medical questions?" card under it lives in Status.vue.
import { computed } from 'vue'
import { Button, LayoutFlex, Text } from '@simple-life-app/growth-ui-kova'
import SupplyProgress from '@/components/cancel/SupplyProgress.vue'
import { paymentMethodLockup } from '@/components/payment/paymentMethodLockups'
import { pastFirstRx, supplyRows, termMonths } from '@/cancelFlow/commitment'
import { formatDate, formatMoney, pluralize } from '@/cancelFlow/format'
import type { Phase, RxStatus } from '@/api/medBilling'
import { useCancelFlowStore } from '@/store/cancelFlow'
import { useSubscriptionStore } from '@/store/subscription'
import { KOVA } from '@/designTokens'

const props = defineProps<{
	/** `false` for a `cancelled` / `declined` plan (decided on the page) — hides Manage subscription. */
	canManage: boolean
}>()

const emit = defineEmits<{
	manage: []
	'change-payment-method': []
	'pay-and-cancel': []
}>()

const store = useSubscriptionStore()
const flow = useCancelFlowStore()

const q = computed(() => flow.quotes)
const sub = computed(() => store.subscription)
const phase = computed(() => sub.value?.phases?.find((p) => p.is_active))
const upfront = computed(() => flow.kind === 'upfront')
const monthly = computed(() => flow.kind === 'monthly')

/** The first prescription in clinical review — not a refill of an approved plan (`pastFirstRx`). */
const underReview = computed(() => flow.stage === 'review')
const rejected = computed(() => store.isRxRejected)
/** Over: nothing ships or bills any more. */
const ended = computed(() => rejected.value || ['cancelled', 'declined'].includes(sub.value?.status ?? ''))
/** Cancelled but still running to its end: renewal turned off, or ending after the instalments. */
const windingDown = computed(() => Boolean(sub.value?.cancelled) && !ended.value)
const endingAfterInstalments = computed(() => sub.value?.cancellation_type === 'supply_end')
/** Cancelled `hard` (under review): access ended with the cancel — `expired_at` is then. */
const endedAtCancel = computed(() => sub.value?.cancellation_type === 'hard')
/** Paid in full, cancelled now with the unshipped supplies refunded (`supply_end`). Figma 6847:86450. */
const refunded = computed(() => upfront.value && Boolean(sub.value?.cancelled) && endingAfterInstalments.value)
/** Paid in full, cancelled at the plan end (`soft`): the paid supplies still come. Figma 7045:40272. */
const paidThrough = computed(
	() =>
		upfront.value &&
		Boolean(sub.value?.cancelled) &&
		!refunded.value &&
		!endedAtCancel.value &&
		!rejected.value &&
		!underReview.value,
)

type Tone = 'brand' | 'neutral' | 'destructive' | 'review'
interface Badge {
	label: string
	tone: Tone
}

const RX_BADGE: Record<RxStatus, Badge> = {
	in_review: { label: 'Clinician review', tone: 'review' },
	approved: { label: 'Approved', tone: 'brand' },
	rejected: { label: 'Rejected', tone: 'destructive' },
}

function money(amount: number | null | undefined) {
	return formatMoney(amount, q.value.currency)
}

/** "12-month plan" — the term in months (12 payments every 4 weeks, or one 48-week period = 12 months). */
const title = computed(() => {
	const months = termMonths(sub.value)
	return months ? `${months}-month plan` : 'Your plan'
})

/** "4 weeks", "6 months", "1 year": a period of whole months (12 weeks and up, 4 weeks a month) reads in months. */
function periodLabel(p: Phase): string | null {
	const n = p.interval_duration
	const unit = p.interval_unit
	if (!n || !unit) return null
	const months = unit === 'month' ? n : unit === 'week' && n >= 12 && n % 4 === 0 ? n / 4 : null
	if (months) return months === 12 ? '1 year' : pluralize(months, 'month')
	return pluralize(n, unit)
}

const billingCycle = computed(() => {
	const p = phase.value
	if (typeof p?.price !== 'number' || !p.interval_duration || !p.interval_unit) return null
	if (monthly.value) {
		const unit = p.interval_duration === 1 ? p.interval_unit : `${p.interval_unit}s`
		return `Billing Cycle · ${money(p.price)} / ${p.interval_duration} ${unit}`
	}
	// Billed per period (paid in full, or a plan whose type isn't known yet): what this period costs —
	// an upfront period's promo sits on its iteration.
	const price = p.iteration_prices?.find((ip) => ip.iteration === sub.value?.billing_period_number)?.price ?? p.price
	const period = periodLabel(p)
	return period ? `Billing Cycle · ${money(price)} / ${period}` : null
})

// Renewal turned off on an approved plan (Figma 7415:15816): "No next charge" replaces the billing line —
// the access end is on the "Expires" pill (design review, 2026-10-22). A monthly commitment that still owes
// payments (`soft_commitment`) bills to its end, so it reads "No charges after {expired_at}" (Anton, design
// review, 2026-10-27). Billed per period, the renewal date follows as one sentence that wraps beside the
// pill (Figma 7515:6598, 6624:13750): "Billing Cycle · $1,548 / 1 year. Next charge: Sep 14, 2027".
const subtitle = computed(() => {
	const renewalOff = paidThrough.value || (windingDown.value && !endingAfterInstalments.value && !underReview.value)
	const end = sub.value?.expired_at
	if (renewalOff && end && monthly.value && sub.value?.cancellation_type === 'soft_commitment')
		return `No charges after ${formatDate(end, 'medium').replace(/ /g, ' ')}` // the date wraps whole
	if (refunded.value || (renewalOff && end)) return 'No next charge'
	if (billingCycle.value && headerNextCharge.value)
		return `${billingCycle.value}. Next charge: ${headerNextCharge.value}`
	return billingCycle.value
})

const planBadge = computed<Badge | null>(() => {
	const s = sub.value
	if (!s) return null
	// A failed renewal keeps `status: active`; only `is_overdue` (engine `InRetry`) says the plan is in debt.
	// It wins over "Expires": renewal off, the plan still bills (DEV-36653).
	if (s.is_overdue && !ended.value) return { label: 'Past due', tone: 'destructive' }
	if (paidThrough.value && s.expired_at)
		return { label: `Expires ${formatDate(s.expired_at, 'short')}`, tone: 'destructive' }
	if (ended.value || (windingDown.value && endingAfterInstalments.value))
		return { label: 'Cancelled', tone: 'destructive' }
	if (windingDown.value) {
		return { label: s.expired_at ? `Expires ${formatDate(s.expired_at, 'short')}` : 'Cancelled', tone: 'destructive' }
	}
	// A refill in review puts an approved plan back in `awaiting_approval`: still active for the customer.
	const refill = pastFirstRx(s)
	if (underReview.value || (s.status === 'awaiting_approval' && !refill)) return { label: 'Pending', tone: 'neutral' }
	if (s.status === 'active' || s.status === 'awaiting_approval') return { label: 'Active', tone: 'brand' }
	if (!s.status) return null
	// "past_due" → "Past due", like the classic card.
	const spaced = s.status.replace(/_/g, ' ')
	return { label: spaced.charAt(0).toUpperCase() + spaced.slice(1), tone: 'neutral' }
})

const prescription = computed<Badge | null>(() => {
	if (rejected.value) return RX_BADGE.rejected
	const rx = sub.value?.rx_status
	// A cancel can take the clinic order back itself (`hard` under review, a paid-in-full refund); the order
	// then reads `rejected`, which says nothing about the prescription — no row. An approved prescription
	// gets no row either (owner, 2026-10-22, as "Hard cancel" 6216:4929 draws it).
	if (!rx || rx === 'rejected' || rx === 'approved') return null
	return RX_BADGE[rx]
})

interface CardSupplyRow {
	number: number
	/** `SUPPLY_LABEL` by state, else "To Be Delivered"; "Cancelled" when refunded unshipped. */
	label: string
	/** Not out of the pharmacy: greyed out. */
	muted: boolean
	/** Won't ship (`cancelled`, or refunded before it shipped): the name struck through (Figma 6847:86450). */
	struck: boolean
	/** The "Fully paid" line under it: every paid-in-full supply but a refunded one, a monthly one paid off. */
	fullyPaid: boolean
	/** The monthly supply being paid for: its "1 / 3 paid" bar. */
	current: boolean
}

/** Billing's supply states with a word of their own; every other one reads "To Be Delivered". */
const SUPPLY_LABEL: Record<string, string> = {
	delivered: 'Delivered',
	shipped: 'In transit',
	delivery_failed: 'Delivery failed',
}

/**
 * The supply rows. None before approval or after a rejection. Monthly commitment: none once the plan is
 * over; while it ends after the instalments only the supply being paid off (Figma 7415:15717). Paid in
 * full: all of the period, after a refund or a cancel at the plan end too — none after a `hard` cancel
 * under review, like the monthly card (nothing was approved).
 */
const supplies = computed<CardSupplyRow[]>(() => {
	if (underReview.value || rejected.value || !flow.kind) return []
	if ((monthly.value || endedAtCancel.value) && ended.value) return []
	const rows = supplyRows(sub.value).map((row) => {
		// Won't ship: billing says so (`cancelled` — the order cancelled, or the supply dropped by a cancel),
		// or the plan was refunded before it shipped.
		const cancelled = row.state === 'cancelled' || (refunded.value && !row.shipped)
		return {
			number: row.number,
			label: cancelled ? 'Cancelled' : (SUPPLY_LABEL[row.state] ?? 'To Be Delivered'),
			muted: !row.shipped,
			struck: cancelled,
			fullyPaid: row.paid && !cancelled,
			current: row.current,
		}
	})
	return monthly.value && endingAfterInstalments.value ? rows.filter((row) => row.current) : rows
})

/** The next charge: when the engine charges (`supply_schedule.next_charge`); off the period end without a schedule. */
const nextCharge = computed(() => {
	const schedule = sub.value?.supply_schedule
	const iso = schedule ? schedule.next_charge?.at : sub.value?.current_period_end_at
	return iso ? formatDate(iso, 'medium') : null
})

const stillBills = computed(() => ['active', 'awaiting_approval'].includes(sub.value?.status ?? ''))

const headerNextCharge = computed(() =>
	!monthly.value && stillBills.value && !sub.value?.cancelled && !rejected.value ? nextCharge.value : null,
)

const showPaymentMethod = computed(() => stillBills.value && !rejected.value && !endingAfterInstalments.value)

const lockup = computed(() => paymentMethodLockup(sub.value?.payment_method))

/** The buy-out still offered to a plan already cancelled in this flow (`instant_fee`). */
const buyOut = computed(() => (sub.value?.cancelled ? q.value.endToday : null))

const showActions = computed(() => Boolean(buyOut.value) || (props.canManage && !rejected.value))

/** When access ends — shown only once the user cancelled. */
const expiration = computed(() => {
	const s = sub.value
	if (!s?.expired_at || rejected.value || !(s.cancelled || s.status === 'cancelled')) return null
	return formatDate(s.expired_at, 'long')
})

type Section = 'header' | 'prescription' | 'supplies' | 'payment' | 'actions' | 'expiration'

const sections = computed<Section[]>(() => {
	const list: Section[] = ['header']
	if (prescription.value) list.push('prescription')
	if (supplies.value.length) list.push('supplies')
	if (showPaymentMethod.value) list.push('payment')
	if (showActions.value) list.push('actions')
	if (expiration.value) list.push('expiration')
	return list
})

/** A divider under every section but the last; the supply rows run straight into the buy-out button. */
function divider(section: Section): string {
	const list = sections.value
	const next = list[list.indexOf(section) + 1]
	if (!next || (section === 'supplies' && next === 'actions')) return 'none'
	return KOVA.divider
}

// Straight under the supply being paid off the buy-out sits 12 below it (Figma 7415:15717); anywhere else
// the button section is 16 / 16 like Manage subscription (7415:20691).
const actionsPadding = computed(() => {
	const list = sections.value
	return list[list.indexOf('actions') - 1] === 'supplies'
		? 'var(--size-12) var(--size-24) var(--size-16)'
		: 'var(--size-16) var(--size-24)'
})

// The frames close the card 16 under the row when the plan is "Cancelled" (7415:17379, 15717, 7493:5749)
// and 24 under it when it "Expires" (renewal off: 7415:15816, 15934), and 24 on paid in full either way
// (6847:86450, 7045:40272) — taken as drawn.
const expirationPadding = computed(() =>
	upfront.value || (windingDown.value && !endingAfterInstalments.value)
		? 'var(--size-16) var(--size-24) var(--size-24)'
		: 'var(--size-16) var(--size-24)',
)
</script>

<style scoped>
/*
  Status pills (Figma "Order statuses": 28px, 14/20 medium, sentence case). The Kova-web `brand/shot` and
  `status/*` colours aren't in the token bundle, so they're literal, as drawn:
  - brand ("Active", "Approved"): `brand/shot/shot-quaternary` #d4f7c4 / `text/accent/green` #00523c;
  - neutral ("Pending"): `button/secondary/grey` navy 8% / `text/black/secondary` navy 55%;
  - destructive ("Cancelled", "Expires …", "Rejected"): `status/error` rgba(203,49,82,.1) / #cb3152;
  - review ("Clinician review"): `status/purple` rgba(162,38,166,.1) / #a226a6.
  Not RxStatusBadge: different type and colours.
*/
.commitment-card__pill {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	height: 28px;
	padding: 6px var(--size-12);
	border-radius: var(--size-96);
	white-space: nowrap;
}

.commitment-card__pill--brand {
	background: #d4f7c4;
	color: #00523c;
}

.commitment-card__pill--neutral {
	background: rgba(50, 54, 83, 0.08);
	color: rgba(50, 54, 83, 0.55);
}

.commitment-card__pill--destructive {
	background: rgba(203, 49, 82, 0.1);
	color: #cb3152;
}

.commitment-card__pill--review {
	background: rgba(162, 38, 166, 0.1);
	color: #a226a6;
}

/* The bare billing line stays on one line and may run on under the pill, which sits beside the name
   only (Figma 7415:15934, "Expires …"); the longer sentences wrap beside it (7415:15816, 7515:6598). */
.commitment-card__one-line {
	white-space: nowrap;
}

/* A refunded supply that never shipped (Figma 6847:86450: "line-through", the font's own decoration). */
.commitment-card__struck {
	text-decoration: line-through;
}

/* Figma: the numbers sit 12 under the supply's line (row gap 8 + 4), the bar 4 under the numbers. */
.commitment-card__progress {
	width: 100%;
	padding-top: var(--size-4);
}

/* The buy-out button is drawn 50 tall (Figma 7415:15717); the DS button is 48. */
.commitment-card__buy-out {
	width: 100%;
}

.commitment-card__buy-out :deep(.button) {
	height: 50px;
}

/* "Update" — a text link in brand green. DS has no link-button, so a native button like
   CancelHeader / OptionRow; flex, so the 22px line box of the label sets its height (Figma 16/22). */
.commitment-card__link {
	appearance: none;
	display: flex;
	padding: 0;
	background: none;
	border: none;
	color: var(--background-brand-high);
	cursor: pointer;
}
</style>
